import * as React from 'react';
import { useIsMobile, useMobileLine } from '../../../hooks/use-mobile';
import { cn } from '../../../utils/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '../drawer';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../sheet';

/**
 * Which family the root mounted, so each part renders the matching piece.
 */
type ResponsiveDialogFamily = 'dialog' | 'drawer' | 'sheet';

const ResponsiveDialogContext = React.createContext<ResponsiveDialogFamily>('dialog');

function useResponsiveDialog(): ResponsiveDialogFamily {
  return React.useContext(ResponsiveDialogContext);
}

const HEADERS = { dialog: DialogHeader, drawer: DrawerHeader, sheet: SheetHeader } as const;

const FOOTERS = { dialog: DialogFooter, drawer: DrawerFooter, sheet: SheetFooter } as const;

/**
 * A centred Dialog above the app's mobile line and, below it, the sheet the
 * app's `MobileLineProvider` asks for: the kit's Drawer, or the bottom Sheet
 * when there is no provider. One set of children for all three.
 */
function ResponsiveDialog(props: React.ComponentProps<typeof Dialog>) {
  const isMobile = useIsMobile();
  const { sheet } = useMobileLine();
  const family = isMobile ? sheet : 'dialog';
  useUnlockBodyOnClose(props.open ?? false);

  const { onOpenChange } = props;
  // Base UI adds its event details; the consumer's contract is Radix's `(open) => void`.
  const handleDrawerOpenChange = React.useCallback(
    (open: boolean) => onOpenChange?.(open),
    [onOpenChange],
  );

  const renderRoot = () => {
    if (family === 'drawer')
      return (
        <Drawer
          defaultOpen={props.defaultOpen}
          modal={props.modal}
          open={props.open}
          onOpenChange={handleDrawerOpenChange}
        >
          {props.children}
        </Drawer>
      );
    if (family === 'sheet') return <Sheet {...props} />;
    return <Dialog {...props} />;
  };

  return <ResponsiveDialogContext value={family}>{renderRoot()}</ResponsiveDialogContext>;
}

/**
 * Radix's scroll-lock can leave `pointer-events: none` on `<body>` after a
 * mid-transition close, freezing the page.
 */
function useUnlockBodyOnClose(open: boolean): void {
  React.useEffect(() => {
    if (open) return;
    const timer = window.setTimeout(() => {
      if (document.body.style.pointerEvents !== 'none') return;
      const openLayer = document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-state="open"][role="menu"]',
      );
      if (!openLayer) document.body.style.pointerEvents = '';
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open]);
}

/**
 * A body-portaled combobox list renders outside the dialog, so its clicks must
 * not dismiss it.
 */
function allowComboboxInteraction(event: {
  target: EventTarget | null;
  preventDefault: () => void;
}) {
  const target = event.target;
  if (target instanceof Element && target.closest('[data-combobox-list]')) event.preventDefault();
}

const ResponsiveDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogContent>,
  React.ComponentPropsWithoutRef<typeof DialogContent>
>(({ className, overlayClassName, overlayStyle, ...props }, ref) => {
  const family = useResponsiveDialog();

  /**
   * A consumer sizes the centred dialog; below the line its width and height
   * classes would pin the sheet, so the sheet's own size is merged in last.
   */
  const classes = {
    drawer: cn(
      className,
      'max-h-(--drawer-content-max-height,none) w-full max-w-none sm:max-w-none',
    ),
    sheet: cn(
      'max-h-[90vh] overflow-y-auto rounded-t-xl',
      className,
      'w-full max-w-none sm:max-w-none',
    ),
  };

  // the sheets draw their own backdrops, so the dialog's backdrop props stay with the dialog
  if (family === 'drawer')
    return (
      <DrawerContent
        ref={ref}
        className={classes.drawer}
        {...props}
      />
    );
  if (family === 'sheet')
    return (
      <SheetContent
        ref={ref}
        side="bottom"
        className={classes.sheet}
        onInteractOutside={allowComboboxInteraction}
        onPointerDownOutside={allowComboboxInteraction}
        {...props}
      />
    );
  return (
    <DialogContent
      ref={ref}
      className={className}
      overlayClassName={overlayClassName}
      overlayStyle={overlayStyle}
      onInteractOutside={allowComboboxInteraction}
      onPointerDownOutside={allowComboboxInteraction}
      {...props}
    />
  );
});
ResponsiveDialogContent.displayName = 'ResponsiveDialogContent';

function ResponsiveDialogHeader(props: React.HTMLAttributes<HTMLDivElement>) {
  const Header = HEADERS[useResponsiveDialog()];
  return <Header {...props} />;
}

function ResponsiveDialogFooter(props: React.HTMLAttributes<HTMLDivElement>) {
  const Footer = FOOTERS[useResponsiveDialog()];
  return <Footer {...props} />;
}

const ResponsiveDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogTitle>,
  React.ComponentPropsWithoutRef<typeof DialogTitle>
>(({ className, face, ...props }, ref) => {
  const family = useResponsiveDialog();

  const classes = {
    drawerTitle: cn({ 'font-display': face === 'display' }, className),
  };

  if (family === 'drawer')
    return (
      <DrawerTitle
        ref={ref}
        className={classes.drawerTitle}
        {...props}
      />
    );
  const Title = family === 'sheet' ? SheetTitle : DialogTitle;
  return (
    <Title
      ref={ref}
      className={className}
      face={face}
      {...props}
    />
  );
});
ResponsiveDialogTitle.displayName = 'ResponsiveDialogTitle';

const ResponsiveDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogDescription>,
  React.ComponentPropsWithoutRef<typeof DialogDescription>
>((props, ref) => {
  const family = useResponsiveDialog();
  if (family === 'drawer')
    return (
      <DrawerDescription
        ref={ref}
        {...props}
      />
    );
  const Description = family === 'sheet' ? SheetDescription : DialogDescription;
  return (
    <Description
      ref={ref}
      {...props}
    />
  );
});
ResponsiveDialogDescription.displayName = 'ResponsiveDialogDescription';

export {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
};
